import React, { useState, useCallback, useEffect } from "react";
import { createChatSocket } from "../utils/chatSocket";
import { useNavigate } from "react-router-dom";
import { QrScanner } from "@yudiel/react-qr-scanner";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import { green } from "@mui/material/colors";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import { parseSupportAttendQuery } from "../lib/rdvBulkInput/supportAppLink";

const socket = createChatSocket();

const DEBUG_QR_ATTEND = true;
function qrAttendLog(...args) {
  if (DEBUG_QR_ATTEND && typeof console !== "undefined") console.log("[QR출퇴근]", ...args);
}

function parseQrUrl(data) {
  const str = (data || "").trim();
  qrAttendLog("스캔 원본", str?.slice(0, 120));
  try {
    let url;
    if (str.startsWith("http://") || str.startsWith("https://")) {
      url = new URL(str);
    } else if (
      str.includes("qrattendmgr") ||
      str.includes("/qra") ||
      str.includes("dsid=") ||
      str.includes("?d=")
    ) {
      const qStart = str.indexOf("?");
      const qs = qStart >= 0 ? str.slice(qStart) : str;
      url = new URL("http://dummy" + (qs.startsWith("?") ? qs : "?" + qs));
    } else {
      qrAttendLog("파싱 스킵: 지원인력 QR URL 아님");
      return null;
    }
    const pathMatch = str.match(/\/(qrattendmgr|qra)(?:\?|$)/);
    const pathname = pathMatch ? `/${pathMatch[1]}` : "";
    const q = parseSupportAttendQuery(url.searchParams, pathname);
    if (!q.dsid) {
      qrAttendLog("파싱 실패: dsid 없음");
      return null;
    }
    if (q.isAttOnlyMode) {
      qrAttendLog("파싱 성공(att-only)", { dsid: q.dsid, att: q.att });
      return {
        dsid: q.dsid,
        att: q.att,
        isAttOnlyMode: true,
        rdvId: null,
        siteId: null,
        dsnm: q.dsnm,
        rname: q.rname,
        isSiteMode: false,
      };
    }
    const isSiteMode = q.isSiteMode;
    const hasRdv = q.rdvId != null && q.rdvId > 0;
    if (isSiteMode) {
      qrAttendLog("파싱 성공(재난현장)", { dsid: q.dsid, siteId: q.siteId, dsnm: q.dsnm, rname: q.rname });
      return { dsid: q.dsid, rdvId: null, siteId: q.siteId, dsnm: q.dsnm, rname: q.rname, isSiteMode: true };
    }
    if (hasRdv) {
      qrAttendLog("파싱 성공(집결지)", { dsid: q.dsid, rdvId: q.rdvId, dsnm: q.dsnm, rname: q.rname });
      return { dsid: q.dsid, rdvId: q.rdvId, siteId: null, dsnm: q.dsnm, rname: q.rname, isSiteMode: false };
    }
    qrAttendLog("파싱 실패: rdv_id 또는 site_id 없음/무효", { rdvId: q.rdvId, siteId: q.siteId });
    return null;
  } catch (e) {
    qrAttendLog("파싱 예외", e?.message ?? e);
    return null;
  }
}

export default function RdvAttendScannerPage(props) {
  const { apiAddr } = props;
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState(null);
  const [pname, setPname] = useState("");
  const [porg, setPorg] = useState("");
  const [pdept, setPdept] = useState("");
  const [prank, setPrank] = useState("");
  const [pphone, setPphone] = useState("");
  const [dutyDesc, setDutyDesc] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  let userData = null;
  try {
    const raw = localStorage.getItem("userData");
    if (raw) {
      const arr = JSON.parse(raw);
      userData = Array.isArray(arr) && arr[0] ? arr[0] : arr;
    }
  } catch (e) {}

  const hasFireUser = userData && userData.i_code;

  useEffect(() => {
    if (scanResult && userData) {
      setPname((userData.i_name || "").trim());
      setPorg((userData.fc_org_nm || "").trim());
      setPdept((userData.i_dept_nm || userData.fc_dpt_nm || "").trim());
      setPrank((userData.i_rank || userData.fc_grade || "").trim());
      setPphone((userData.i_mphone || "").trim());
    }
  }, [scanResult, userData?.i_name, userData?.fc_org_nm, userData?.i_dept_nm, userData?.i_rank, userData?.i_mphone]);

  const onScan = useCallback(
    (result) => {
      if (scanResult) return;
      const parsed = parseQrUrl(result);
      if (parsed) {
        qrAttendLog("스캔 인식됨", parsed);
        setScanResult(parsed);
      } else {
        qrAttendLog("스캔 인식 실패(파라미터 부족 또는 형식 오류)");
      }
    },
    [scanResult]
  );

  const handleSubmit = async () => {
    if (!apiAddr || !scanResult || !hasFireUser) return;
    const name = (pname || userData.i_name || "").trim();
    if (!name) {
      setSubmitError("성명을 입력해주세요.");
      return;
    }
    setSubmitError("");
    setSubmitLoading(true);
    const locationField = scanResult.isSiteMode && scanResult.siteId != null
      ? { rdv_id: 0, site_id: scanResult.siteId }
      : { rdv_id: scanResult.rdvId };
    qrAttendLog("출근 등록 요청", { ...locationField, dsid: scanResult.dsid });
    try {
      const res = await fetch(`${apiAddr}/post/rdvcheckin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dsid: scanResult.dsid,
          ...locationField,
          person_type: "fire",
          pname: name,
          porg: (porg || userData.fc_org_nm || "").trim(),
          pdept: (pdept || userData.i_dept_nm || userData.fc_dpt_nm || "").trim(),
          prank: (prank || userData.i_rank || userData.fc_grade || "").trim(),
          insa_code: (userData.i_code || "").trim(),
          pphone: (pphone || userData.i_mphone || "").trim(),
          duty_desc: dutyDesc.trim(),
        }),
      });
      const data = await res.json();
      qrAttendLog("출근 등록 응답", data);
      if (data.result === "success") {
        setScanResult(null);
        socket.emit("rdvattendance", { dsid: scanResult.dsid });
        setPname("");
        setPorg("");
        setPdept("");
        setPrank("");
        setPphone("");
        setDutyDesc("");
        alert("출근 등록이 완료되었습니다.");
        navigate(-1);
      } else {
        setSubmitError(data.message || "출근 처리에 실패했습니다.");
      }
    } catch (e) {
      qrAttendLog("출근 등록 예외", e?.message ?? e);
      setSubmitError("전송 중 오류가 발생했습니다.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!apiAddr || !scanResult || !hasFireUser) return;
    setSubmitError("");
    setCheckoutLoading(true);
    const listUrl = scanResult.isSiteMode && scanResult.siteId != null
      ? `${apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(scanResult.dsid)}&site_id=${scanResult.siteId}`
      : `${apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(scanResult.dsid)}&rdv_id=${scanResult.rdvId}`;
    qrAttendLog("퇴근 목록 조회", listUrl);
    try {
      const res = await fetch(listUrl);
      const list = await res.json();
      const mine = Array.isArray(list)
        ? list.find(
            (r) =>
              r.person_type === "fire" &&
              (r.insa_code && String(r.insa_code).trim() === String(userData.i_code).trim()) &&
              !r.check_out_dt
          )
        : null;
      if (!mine) {
        setSubmitError("퇴근 처리할 출근 기록이 없습니다.");
        return;
      }
      const outRes = await fetch(`${apiAddr}/post/rdvcheckout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snum: mine.snum }),
      });
      const outData = await outRes.json();
      qrAttendLog("퇴근 등록 응답", outData);
      if (outData.result === "success") {
        setScanResult(null);
        socket.emit("rdvattendance", { dsid: scanResult.dsid });
        alert("퇴근 등록이 완료되었습니다.");
        navigate(-1);
      } else {
        setSubmitError(outData.message || "퇴근 처리에 실패했습니다.");
      }
    } catch (e) {
      qrAttendLog("퇴근 처리 예외", e?.message ?? e);
      setSubmitError("퇴근 처리 중 오류가 발생했습니다.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleReset = () => {
    setScanResult(null);
    setPname("");
    setPorg("");
    setPdept("");
    setPrank("");
    setPphone("");
    setDutyDesc("");
    setSubmitError("");
  };

  if (!hasFireUser) {
    return (
      <Box sx={{ minHeight: "100vh", p: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5" }}>
        <Paper sx={{ p: 3, maxWidth: 400, textAlign: "center" }}>
          <Typography color="error" gutterBottom>
            자원등록앱 로그인 후 사용해 주세요.
          </Typography>
          <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            자원등록앱으로
          </Button>
        </Paper>
      </Box>
    );
  }

  if (scanResult) {
    return (
      <Box sx={{ minHeight: "100vh", p: 2, bgcolor: "#f5f5f5", maxWidth: 480, mx: "auto" }}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: green[700], color: "white" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            지원인력 출근 등록 (소방공무원)
          </Typography>
          <Typography variant="body2">
            {[scanResult.dsnm, scanResult.rname].filter(Boolean).join(" / ") || (scanResult.isSiteMode ? "재난현장 지원인력" : "지원인력 관리")}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>기본인적사항</Typography>
          <TextField fullWidth size="small" label="기관" value={porg} onChange={(e) => setPorg(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="부서" value={pdept} onChange={(e) => setPdept(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="계급" value={prank} onChange={(e) => setPrank(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="성명 *" required value={pname} onChange={(e) => setPname(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="연락처" value={pphone} onChange={(e) => setPphone(e.target.value)} sx={{ mb: 1.5 }} />
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>수행임무</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="수행임무를 입력하세요"
            value={dutyDesc}
            onChange={(e) => setDutyDesc(e.target.value)}
            sx={{ mb: 2 }}
          />
          {submitError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {submitError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleReset}>
              다시 스캔
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: green[700], "&:hover": { bgcolor: green[800] } }}
              onClick={handleSubmit}
              disabled={submitLoading || checkoutLoading}
            >
              {submitLoading ? <CircularProgress size={24} /> : "출근 등록"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleCheckOut}
              disabled={submitLoading || checkoutLoading}
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
            >
              {checkoutLoading ? <CircularProgress size={24} /> : "퇴근 등록"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 2, bgcolor: "#f5f5f5" }}>
      <Paper sx={{ p: 2, mb: 2, bgcolor: green[700], color: "white" }}>
        <Typography variant="h6">지원인력 QR 출퇴근 관리</Typography>
        <Typography variant="body2">지원인력관리 모달의 QR을 스캔하세요</Typography>
      </Paper>
      <Box sx={{ maxWidth: 400, mx: "auto" }}>
        <QrScanner
          onDecode={onScan}
          onError={(e) => qrAttendLog("카메라/스캔 오류", e?.message ?? e)}
          constraints={{ facingMode: "environment" }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setScanResult(null)}>
          초기화
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          자원등록앱으로
        </Button>
      </Box>
    </Box>
  );
}
